import { formatDate } from "./format";

// One plain label (or sentence) for every value of the service's closed lists the ops screens show.
// A value this file does not know is shown as itself, so a new service value never breaks a screen.
const pick = (table) => (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return Object.prototype.hasOwnProperty.call(table, value) ? table[value] : String(value);
};

export const SIGNAL_TEXT = {
  payment_unconfirmed: {
    label: "Payments not confirmed",
    sentence: "Money may have been taken, but the provider has not confirmed the payment.",
  },
  refund_failed: {
    label: "Refunds that failed",
    sentence: "A refund was asked for, but the provider did not complete it.",
  },
  outbox_dead: {
    label: "Stopped background jobs",
    sentence: "A job that updates another system gave up after many tries.",
  },
  partner_not_configured: {
    label: "No insurer set up",
    sentence: "Cover was bought, but no insurance partner is set up to issue it.",
  },
  partner_api_failed: {
    label: "Insurer request failed",
    sentence: "The request to the insurer could not be sent after many tries.",
  },
  partner_policy_late: {
    label: "Policy is late",
    sentence: "The insurer accepted the request but has not issued the policy in time.",
  },
  partner_policy_rejected: {
    label: "Policy refused",
    sentence: "The insurer refused to issue the policy, and the rider has paid for it.",
  },
  partner_policy_never_activated: {
    label: "Cover never activated",
    sentence: "Cover was bought but never became active. Nothing to do from here.",
  },
  cover_refund_failed: {
    label: "Cover refunds that failed",
    sentence:
      "A refund of a theft or roadside cover was turned down by the payment provider, or came back with a different amount.",
  },
};

export const signalLabel = (signal) => SIGNAL_TEXT[signal]?.label || pick({})(signal);
export const signalSentence = (signal) => SIGNAL_TEXT[signal]?.sentence || "";

// The partner signals, in the order of the Partner policies tabs.
export const PARTNER_SIGNALS = [
  "partner_not_configured",
  "partner_api_failed",
  "partner_policy_late",
  "partner_policy_rejected",
  "partner_policy_never_activated",
  "cover_refund_failed",
];

export const isPartnerSignal = (signal) => PARTNER_SIGNALS.includes(signal);

// The button of each resolving action, the dialog title and what the action does in words.
export const ACTION_TEXT = {
  recheck_payment: {
    label: "Re-check payment",
    title: "Re-check this payment",
    effect: "This asks the provider again whether the payment went through. Nothing is charged or refunded.",
    done: "Re-check queued",
  },
  retry_refund: {
    label: "Retry refund",
    title: "Retry this refund",
    effect: "This asks the provider to complete the refund again. Money goes back to the rider if it works.",
    done: "Refund retry queued",
  },
  retry_outbox: {
    label: "Retry job",
    title: "Retry this job",
    effect: "This puts the stopped job back in the queue so it runs again.",
    done: "Job retry queued",
  },
  retry_partner_policy: {
    label: "Retry request",
    title: "Retry the insurer request",
    effect: "This sends the policy request to the insurer again.",
    done: "Request queued",
  },
  ask_partner_again: {
    label: "Ask the insurer again",
    title: "Ask the insurer again",
    effect: "This asks the insurer for the policy it has already accepted.",
    done: "Insurer asked again",
  },
  refund_cover: {
    label: "Refund cover",
    title: "Refund this cover",
    effect: "This gives the rider their money back for this cover and ends the cover.",
    done: "Cover refund queued",
  },
  // A cover refund the provider answered with a different amount. Nothing is sent to the provider:
  // staff record what its dashboard shows (SettleCoverRefundDialog).
  settle_cover_refund: {
    label: "Settle after checking the provider",
    title: "Settle this cover refund",
    effect:
      "The payment provider answered this cover refund with a different amount. Check the refund on the provider dashboard first, then record what it shows. Nothing is sent to the provider.",
    done: "Cover refund settled",
  },
};

export const actionLabel = (action) => ACTION_TEXT[action]?.label || pick({})(action);

// Plain words for staff (27 Oct 2026): a one-time plan paid in full is `completed`, which read as
// "over"; it is the most common covered state.
export const subscriptionStatusLabel = pick({
  pending: "Checkout not paid",
  active: "Active",
  paused: "Payment missed",
  cancelling: "Ending soon",
  cancelled: "Cancelled",
  completed: "Paid in full",
  ended: "Cover ended",
  refunded: "Refunded",
});

// Why a subscription in the list needs a look (attention reasons of listSupportSubscriptions).
export const attentionLabel = pick({
  paused: "Paused",
  overdue: "Overdue",
  payment_unconfirmed: "Payment not confirmed",
});

export const paymentStatusLabel = pick({
  created: "Not confirmed",
  captured: "Paid",
  failed: "Failed",
  refunded: "Refunded",
});

export const paymentPurposeLabel = pick({
  first: "First payment",
  instalment: "Instalment",
  pay_now: "Pay now",
  upgrade_difference: "Upgrade difference",
});

export const paymentLineKindLabel = pick({
  plan: "Plan",
  instalment: "Instalment",
  upfront_benefit: "Cover bought up front",
  upgrade_difference: "Upgrade difference",
});

export const refundReasonLabel = pick({
  cooling_off: "Cooling-off refund",
  double_charge: "Charged twice",
  upgrade_not_applied: "Upgrade not applied",
  plan_not_live: "Plan was not live",
  rider_request: "Rider request",
  sold_in_error: "Sold in error",
});

export const REFUND_REQUEST_REASONS = ["rider_request", "sold_in_error"];

export const entitlementStateLabel = pick({
  locked: "Locked",
  active: "Active",
  paused: "Paused",
  stopped: "Stopped",
  consumed: "Used",
  pending_activation: "Waiting to be activated",
  policy_requested: "Waiting for the insurer",
  policy_rejected: "Insurer refused",
});

// why: codes for free services and coupons are created before the benefit unlocks, so "has a code"
// alone reads as already issued; the label says whether the rider can use it now.
const CODE_USABLE_STATES = new Set(["active", "consumed"]);
const CODE_WAITING_STATES = new Set(["locked", "pending_activation", "policy_requested"]);

export const entitlementCodeLabel = (state, hasCode) => {
  if (!hasCode) {
    return "";
  }
  if (CODE_USABLE_STATES.has(state)) {
    return "Code issued";
  }
  if (CODE_WAITING_STATES.has(state)) {
    return "Code ready, unlocks later";
  }
  return "Code held, not usable now";
};

export const benefitKindLabel = pick({
  motor_extension: "Motor extension",
  battery_extension: "Battery extension",
  free_service: "Free service",
  theft: "Theft cover",
  roadside: "Roadside cover",
  coupon: "Coupon",
});

export const chargeModelLabel = pick({
  upfront: "Paid up front",
  monthly: "Paid monthly",
  included: "Included",
});

const EXTENSION_KINDS = new Set(["motor_extension", "battery_extension"]);

// why (30 Oct 2026, Sachin): an extension row has no end date of its own; the service sends the
// cover it adds (the day after standard cover ends to the end of the months granted).
export const entitlementValidLabel = (item) => {
  if (item.coverFrom && item.coverUntil) {
    return `${formatDate(item.coverFrom)} to ${formatDate(item.coverUntil)}`;
  }
  if (EXTENSION_KINDS.has(item.kind)) {
    return "No extra cover yet";
  }
  return item.validFrom || item.validTo ? `${formatDate(item.validFrom)} to ${formatDate(item.validTo)}` : "-";
};

// why (30 Oct 2026, Sachin): "monthly" is how the plan bills the benefit on monthly billing; on a
// one-payment plan the whole term was paid at the start.
export const entitlementPaidLabel = (chargeModel, billingMode) =>
  chargeModel === "monthly" && billingMode === "one_time" ? "Paid up front" : chargeModelLabel(chargeModel);

export const billingModeLabel = pick({
  one_time: "One payment",
  monthly: "Monthly",
});

export const dateSourceLabel = pick({
  order_invoice: "Order invoice",
  dealer_entry: "Entered by the dealer",
  invoice_ocr: "Read from the invoice",
  customer_claim: "Typed by the rider",
  migrated: "Migrated record",
  staff_verified: "Verified by staff",
  staff_review: "Read off the invoice by staff",
});

export const reviewReasonLabel = pick({
  no_sale_record: "No sale record found",
  dealer_sale_needs_invoice: "Dealer sale needs an invoice",
  invoice_needs_review: "Invoice needs a check",
  ownership_claim: "Ownership claim",
  product_unresolved: "Product not matched",
  legacy_status: "Old record, status unclear",
  legacy_phone_invalid: "Old record, phone not valid",
  legacy_frame_shape: "Old record, frame number odd",
  legacy_late_row: "Old record, arrived late",
  legacy_missing_frame: "Old record, no frame number",
  date_cross_check_failed: "Purchase date did not match",
  needs_ocr: "Invoice not read yet",
  ocr_uncorroborated: "Invoice read, not confirmed",
});

export const partnerStateLabel = pick({
  not_configured: "No insurer set up",
  requested: "Request sent",
  accepted: "Waiting for the insurer",
  issued: "Policy issued",
  rejected: "Insurer refused",
  failed: "Request failed",
  cancelled: "Cancelled",
});

// Monthly partner settlement: what each statement line is, and which refund made a refund line.
export const settlementLineTypeLabel = pick({
  paid: "Paid",
  refund: "Refund",
  total: "Total",
});

export const settlementRefundKindLabel = pick({
  cover_refund: "Cover refunded",
  payment_refund: "Whole payment refunded",
});

export const requestStateLabel = pick({
  requested: "In progress",
  refunded: "Refunded",
  failed: "Failed",
  mismatch: "A different amount came back",
});

export const registrationStatusLabel = pick({
  pending_review: "Waiting for review",
  active: "Active",
  rejected: "Rejected",
});

// The two statuses of the awaiting invoice list: the rider chose to upload the invoice later.
export const awaitingStatusLabel = pick({
  awaiting_invoice: "Awaiting invoice",
  expired: "Expired",
});

// How a registration came in (registered_via in the warranty service).
export const registeredViaLabel = pick({
  rider_app: "Rider app",
  dealer_app: "Dealer app",
  website: "Website",
  import: "Import",
});

// Who sold a plan or a part (the warranty service's salesChannel). Every row from before the support
// bot sold anything is `app`; a value not listed here is shown as itself.
export const salesChannelLabel = pick({
  app: "App",
  chat_bot: "Support bot",
});

// Chat orders: the battery and charger orders the support bot placed.
export const partLabel = pick({ battery: "Battery", charger: "Charger" });

export const coveredLabel = (covered) => (covered === true ? "Covered" : covered === false ? "Paid" : "-");

export const deliveryKindLabel = pick({ dealer: "Dealer", home: "Home" });

export const partOrderStatusLabel = pick({
  offered: "Offered",
  placing: "Placing with the OMS",
  awaiting_payment: "Awaiting payment",
  paid: "Paid, OMS order pending",
  placed: "Placed",
  refused: "Refused by the OMS",
  refunding: "Refunding",
  refunded: "Refunded",
  cancelled: "Cancelled",
});

// The event kinds of a part order's history. The service may send them with or without the
// `part_order.` prefix of its own event table.
const PART_ORDER_EVENT_TEXT = {
  offered: "Offered",
  ordered: "Ordered",
  checkout_created: "Checkout opened",
  payment_captured: "Payment received",
  payment_failed: "Payment failed",
  oms_order_requested: "OMS order asked for",
  oms_order_created: "OMS order created",
  oms_order_refused: "OMS refused the order",
  refund_requested: "Refund asked for",
  refunded: "Refunded",
  bot_told: "Rider told in chat",
};
export const partOrderEventLabel = (kind) =>
  pick(PART_ORDER_EVENT_TEXT)(typeof kind === "string" ? kind.replace(/^part_order\./, "") : kind);

// Who did it: `ai` (the support bot), the rider's account, or `system`. The actor may come as a word
// or as { actorType, actorId }; the id is not shown here.
const ACTOR_TEXT = { ai: "Support bot", rider: "Rider", account: "Rider", system: "System", staff: "Staff" };
export const partOrderActorLabel = (actor) => {
  const kind = actor !== null && typeof actor === "object" ? actor.actorType || actor.kind : actor;
  return pick(ACTOR_TEXT)(kind);
};

// Invoice check failure codes are shown as they are, in plain words where we know them.
export const invoiceFailureLabel = pick({
  date_mismatch: "The date on the invoice does not match the date the rider typed",
  frame_mismatch: "The frame number on the invoice does not match",
});

export const invoiceCheckStateLabel = pick({
  review: "Needs a person to check",
  verified: "Checked and fine",
  rejected: "Refused",
});

// Why a cooling-off refund waits on a mismatched cover refund, and what unblocks it. Waiting does not
// clear it; a person settles it after checking the provider.
export const COVER_REFUND_MISMATCH_SENTENCE =
  "A cover refund came back from the payment provider with a different amount, so this refund is blocked. Settle it in Partner policies, Cover refunds that failed (Settle after checking the provider); then this refund can go ahead.";

// Refund check rows: a tick sentence and a cross sentence per check; `detail` is a code or a date.
const dmy = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : null;

export const refundCheckText = (check, windowEndsOn) => {
  switch (check.name) {
    case "status":
      return check.ok
        ? "The subscription is in a state that can be refunded."
        : `The subscription is ${subscriptionStatusLabel(check.detail).toLowerCase()}, so a cooling-off refund is not possible.`;
    case "window":
      return check.ok
        ? windowEndsOn
          ? `Still inside the cooling-off window, which ends on ${dmy(windowEndsOn) || windowEndsOn}.`
          : "Still inside the cooling-off window."
        : `The cooling-off window closed on ${dmy(check.detail) || check.detail || "an earlier date"}.`;
    case "no_use":
      return check.ok
        ? "The rider has not used any benefit."
        : "The rider has already used a benefit (a service, coupon or cover).";
    case "coupon_state":
      return check.ok
        ? "The coupon can be cancelled."
        : check.detail === "unavailable"
        ? "The coupon system could not be reached, so the coupon cannot be checked."
        : "The coupon has already been used.";
    case "policy_issued":
      return check.ok
        ? "No theft or roadside policy has been issued for this plan."
        : "A theft or roadside policy has been issued for this plan.";
    case "cover_refund_in_flight":
      return check.ok
        ? "No cover refund is in progress."
        : check.detail === "mismatch"
        ? COVER_REFUND_MISMATCH_SENTENCE
        : "A cover refund is already in progress for this payment.";
    default:
      return `${check.name}: ${check.ok ? "ok" : check.detail || "not ok"}`;
  }
};

// Keys of a partner's request and response summary, in words. An unknown key is shown as itself.
export const summaryKeyLabel = pick({
  requestId: "Request id",
  benefitKind: "Cover",
  coverFrom: "Cover from",
  coverTo: "Cover to",
  amountMinor: "Amount",
  taxMinor: "Tax",
  currency: "Currency",
  kind: "Answer",
  code: "Code",
  reference: "Insurer reference",
});
